// Shared/dropDownStyles.tsx

import { StylesConfig } from "react-select";

type OptionType = {
  label: string;
  value: string;
};

// Shared base styles
const baseOptionStyles = {
  fontSize: "14px",
  fontWeight: 500,
  color: "#444",
  borderRadius: "8px",
  transition: "background 0.2s ease",
  cursor: "pointer",
};

const getOptionStyles = (provided: any, state: any) => ({
  ...provided,
  ...baseOptionStyles,
  backgroundColor: state.isSelected
    ? "#83ffc1" // selected
    : state.isFocused
      ? "#83ffc1" // focused
      : "#fff",
  color: state.isDisabled ? "#ccc" : state.isSelected ? "#232323" : "#232323",
  cursor: state.isDisabled ? "not-allowed" : "pointer",
  ":active": {
    backgroundColor: !state.isDisabled
      ? state.isSelected
        ? "#83ffc1"
        : "#83ffc1"
      : undefined,
  },
});

const getControlStyles = (width = "50px", isError = false) => ({
  height: "45px",
  width,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  backgroundColor: "#fff",
  padding: "10px 8px",
  borderRadius: "6px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.017)",
  border: isError ? "1px solid red" : "1px solid #e8e8e8",
  fontSize: "14px",
  fontWeight: 500,
  color: "#202020",
  fontFamily: "'Poppins', sans-serif",
  // background: "red",
});

const baseMenuListStyles = {
  backgroundColor: "#fff",
  borderRadius: "12px",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.052)",
  padding: "8px",
  zIndex: 999,
  maxHeight: "250px",
  overflowY: "auto",
};

const indicatorSeparator = () => ({ display: "none" });

const placeholderStyles = (styles: any) => ({
  ...styles,
  color: "#aaa",
  fontWeight: 400,
});

const singleValueStyles = (styles: any) => ({
  ...styles,
  color: "#202020",
  fontWeight: 500,
});

// === Variants ===

const customStylesForCountry = {
  control: () => getControlStyles("100%"),
  option: getOptionStyles,
  menuList: (provided: any) => ({
    ...provided,
    ...baseMenuListStyles,
  }),
  placeholder: placeholderStyles,
  singleValue: singleValueStyles,
  indicatorSeparator,
};

const customStylesForCountryError = {
  ...customStylesForCountry,
  control: () => getControlStyles("100%", true),
};

const customStylesForDays = {
  control: () => getControlStyles("120px"),
  option: getOptionStyles,
  menuList: (provided: any) => ({
    ...provided,
    ...baseMenuListStyles,
  }),
  indicatorSeparator,
};

const customStylesForDaysError = {
  ...customStylesForDays,
  control: () => getControlStyles("120px", true),
};

const customStylesForMonths = {
  control: () => getControlStyles("120px"),
  option: getOptionStyles,
  menuList: (provided: any) => ({
    ...provided,
    ...baseMenuListStyles,
  }),
  indicatorSeparator,
};

const customStylesForMonthsError = {
  ...customStylesForMonths,
  control: () => getControlStyles("120px", true),
};

const customStylesForYears = {
  control: () => getControlStyles("120px"),
  option: getOptionStyles,
  menuList: (provided: any) => ({
    ...provided,
    ...baseMenuListStyles,
  }),
  indicatorSeparator,
};

const customStylesForYearsError = {
  ...customStylesForYears,
  control: () => getControlStyles("104px", true),
};

const customStylesForFinancialYears = {
  control: () => getControlStyles("252px"),
  option: getOptionStyles,
  menuList: (provided: any) => ({
    ...provided,
    ...baseMenuListStyles,
  }),
  indicatorSeparator,
};

const customStylesForTaxMode = {
  control: () => getControlStyles("300px"),
  option: getOptionStyles,
  menuList: (provided: any) => ({
    ...provided,
    ...baseMenuListStyles,
  }),
  indicatorSeparator,
};

const customStylesForTaxModeError = {
  ...customStylesForTaxMode,
  control: () => getControlStyles("300px", true),
};

const customStylesForDashboard: StylesConfig<OptionType, false> = {
  control: () => ({
    ...getControlStyles("100px"),
    height: "40px",
    padding: "0 10px",
    borderRadius: "4px",
    fontSize: "12px",
    margin: "2px",
  }),
  option: (styles, state) => ({
    ...getOptionStyles(styles, state),
    fontSize: "12px",
  }),
  menuList: (styles) => ({
    ...styles,
    ...baseMenuListStyles,
  }) as import("react-select").CSSObjectWithLabel,
  placeholder: placeholderStyles,
  singleValue: singleValueStyles,
  indicatorSeparator,
};


export default {
  customStylesForCountry,
  customStylesForCountryError,
  customStylesForMonths,
  customStylesForMonthsError,
  customStylesForYears,
  customStylesForYearsError,
  customStylesForDays,
  customStylesForDaysError,
  customStylesForFinancialYears,
  customStylesForTaxMode,
  customStylesForTaxModeError,
  customStylesForDashboard,
};
